import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AccessControlDoorType,
  LockResponse,
  SiteSystemType,
} from "api/ApiClient";
import { isFirmwarePanelVisible } from "../lockDisplay";
import axios from "axios";
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { ChevronDown, Pencil, Trash2 } from "lucide-react";
import { Button } from "components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { BASE_API_URL } from "store/Me";

export default function Details({
  lock,
  onDeleteClick,
}: {
  lock: LockResponse;
  onDeleteClick?: () => void;
}) {
  const [isEditOpen, setIsEditOpen] = useState(false);

  return (
    <>
      <section className="overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200">
        <header className="flex items-center justify-between gap-4 px-4 py-3 border-b border-slate-200">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Lock details</h2>
            <p className="mt-0.5 text-xs text-slate-500">Configuration and hardware</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label={`Edit ${lock.name}`}
                  className="text-primary hover:text-primary"
                  onClick={() => setIsEditOpen(true)}
                  size="icon-sm"
                  type="button"
                  variant="ghost"
                >
                  <Pencil />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit lock</TooltipContent>
            </Tooltip>
            {onDeleteClick ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label={`Delete ${lock.name}`}
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={onDeleteClick}
                    size="icon-sm"
                    type="button"
                    variant="ghost"
                  >
                    <Trash2 />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete lock</TooltipContent>
              </Tooltip>
            ) : null}
          </div>
        </header>
        <ShowDetails lock={lock} />
      </section>

      <EditDetailsModal
        lock={lock}
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
      />
    </>
  );
}

export function formatDoorType(doorType: string | undefined): string {
  if (!doorType) {
    return "-";
  }

  return doorType.charAt(0).toUpperCase() + doorType.slice(1);
}

function DetailItem({
  label,
  mono,
  children,
}: {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-4 px-4 py-3">
      <dt className="text-xs font-semibold tracking-wide uppercase text-slate-500">
        {label}
      </dt>
      <dd
        className={`min-w-0 text-sm font-medium wrap-break-word text-slate-800 ${mono ? "font-mono" : ""}`}
      >
        {children}
      </dd>
    </div>
  );
}

const LOCK_SYNC_FLAGS = [
  { id: 1, name: "Audits (download)", short: "Aud" },
  { id: 2, name: "Config (download)", short: "Cfg" },
  { id: 4, name: "Database (upsert to server)", short: "DB" },
];

function ShowDetails({
  lock,
}: {
  lock: LockResponse;
}) {
  const [flags, setFlags] = useState<number[]>([1, 2, 4]);
  const [syncMenuOpen, setSyncMenuOpen] = useState(false);
  const syncLockMutation = usePostSyncLock(lock.id!);

  const toggleFlag = (flag: number) => {
    if (flags.includes(flag)) {
      setFlags(flags.filter((f) => f !== flag));
    } else {
      setFlags([...flags, flag]);
    }
  };

  const syncLock = async () => {
    const flagsValue = flags.reduce((acc, f) => acc | f, 0);
    await syncLockMutation.mutateAsync(flagsValue);
    setSyncMenuOpen(false);
  };

  const selectedShort = LOCK_SYNC_FLAGS.filter((f) => flags.includes(f.id)).map((f) => f.short);
  const syncLabel = syncLockMutation.isPending
    ? "Syncing..."
    : selectedShort.length === LOCK_SYNC_FLAGS.length
    ? "Sync All"
    : selectedShort.length === 0
    ? "Sync"
    : `Sync (${selectedShort.join(", ")})`;

  const showSync =
    lock.site?.account?.systemType === SiteSystemType.SchlageEngage &&
    lock.deviceType === "cte";

  return (
    <div>
      <dl className="divide-y divide-slate-100">
        {lock.deviceType && !isFirmwarePanelVisible(lock) ? (
          // Shown here only when the Firmware & Hardware panel (which normally carries Device Type) isn't rendered.
          <DetailItem label="Device Type" mono>
            {lock.deviceType.toUpperCase()}
          </DetailItem>
        ) : null}
        {isFirmwarePanelVisible(lock) ? (
          <DetailItem label="No Tour Enabled">
            {lock.noTourEnabled ? "Yes" : "No"}
          </DetailItem>
        ) : null}
        {isFirmwarePanelVisible(lock) ? (
          <DetailItem label="Audit Invalid Credentials">
            {lock.auditInvalidCredentials == null ? "Not set" : lock.auditInvalidCredentials ? "Enabled" : "Disabled"}
          </DetailItem>
        ) : null}
      </dl>
      {showSync ? (
        <div className="flex flex-col items-end gap-2 px-4 py-3 border-t border-slate-100">
          {lock.onlineStatus === "Offline" && (
            <span className="text-xs text-destructive">
              Lock is offline — sync unavailable
            </span>
          )}
          {flags.length === 0 && lock.onlineStatus !== "Offline" && (
            <span className="text-xs text-muted-foreground">
              Select at least one option to sync
            </span>
          )}
          <DropdownMenu open={syncMenuOpen} onOpenChange={setSyncMenuOpen}>
            <div className="inline-flex">
              <Button
                onClick={syncLock}
                className="rounded-r-none"
                disabled={syncLockMutation.isPending || flags.length === 0 || lock.onlineStatus === "Offline"}
                size="sm"
                type="button"
              >
                {syncLabel}
              </Button>
              <DropdownMenuTrigger asChild>
                <Button
                  aria-label="Choose lock sync options"
                  className="border-l rounded-l-none border-l-primary-foreground/30"
                  disabled={syncLockMutation.isPending || lock.onlineStatus === "Offline"}
                  size="icon-sm"
                  type="button"
                >
                  <ChevronDown />
                </Button>
              </DropdownMenuTrigger>
            </div>
            <DropdownMenuContent align="end">
              {LOCK_SYNC_FLAGS.map((item) => (
                <DropdownMenuCheckboxItem
                  checked={flags.includes(item.id)}
                  key={item.id}
                  onSelect={(event) => {
                    event.preventDefault();
                    toggleFlag(item.id);
                  }}
                >
                  {item.name}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}
    </div>
  );
}

function EditDetailsModal({
  lock,
  open,
  onClose,
}: {
  lock: LockResponse;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const putLockMutation = usePutLock(lock.id!);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      const form = e.currentTarget;
      const data: Pick<LockResponse, "name" | "doorType" | "noTourEnabled"> & {
        auditInvalidCredentials?: boolean;
      } = {
        name: form.lockName.value,
        doorType: form.doorType.value,
      };

      if (lock.site?.account?.systemType === SiteSystemType.SchlageEngage) {
        const noTourInput = form.elements.namedItem("noTourEnabled") as HTMLInputElement | null;
        const auditInput = form.elements.namedItem("auditInvalidCredentials") as HTMLInputElement | null;

        if (noTourInput) {
          data.noTourEnabled = noTourInput.checked;
        }

        if (auditInput) {
          data.auditInvalidCredentials = auditInput.checked;
        }
      }

      await putLockMutation.mutateAsync(data);
      onClose();
      queryClient.invalidateQueries({ queryKey: ["locks"] });
    } catch (e) {
      console.log("ERROR ", e);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        showCloseButton={false}
        className="sm:max-w-lg"
      >
        <form onSubmit={onSubmit}>
          <fieldset className="grid gap-4" disabled={putLockMutation.isPending}>
            <DialogHeader>
              <DialogTitle>Edit Lock</DialogTitle>
            </DialogHeader>
            <div>
              <label htmlFor="lockName">Name</label>
              <input
                id="lockName"
                className="form-control"
                defaultValue={lock.name}
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <label htmlFor="doorType">Door Type</label>
              <select
                id="doorType"
                className="form-control"
                defaultValue={lock.doorType}
              >
                {Object.keys(AccessControlDoorType)
                  .filter((k) => isNaN(Number(k)))
                  .map((k, i) => (
                    <option key={i} value={k.toLowerCase()}>
                      {k}
                    </option>
                  ))}
              </select>
            </div>
            {isFirmwarePanelVisible(lock) ? (
              <div style={{ marginTop: 12 }}>
                <label htmlFor="noTourEnabled">
                  <input
                    id="noTourEnabled"
                    name="noTourEnabled"
                    type="checkbox"
                    defaultChecked={lock.noTourEnabled}
                  />{" "}
                  No Tour Enabled
                </label>
              </div>
            ) : null}
            {isFirmwarePanelVisible(lock) ? (
              <div style={{ marginTop: 12 }}>
                <label htmlFor="auditInvalidCredentials">
                  <input
                    id="auditInvalidCredentials"
                    name="auditInvalidCredentials"
                    type="checkbox"
                    defaultChecked={lock.auditInvalidCredentials ?? false}
                  />{" "}
                  Audit Invalid Credentials
                </label>
              </div>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">
                {putLockMutation.isPending ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </fieldset>
        </form>
      </DialogContent>
    </Dialog>
  );
}

async function putLock(
  lockId: string,
  data: Pick<LockResponse, "name" | "doorType" | "noTourEnabled"> & { auditInvalidCredentials?: boolean },
) {
  const result = await axios.put<LockResponse>(
    `${BASE_API_URL}/api/locks/${lockId}`,
    data,
  );
  return result.data;
}

function usePutLock(lockId: string) {
  return useMutation({
    mutationFn: (
      lock: Pick<LockResponse, "name" | "doorType" | "noTourEnabled"> & { auditInvalidCredentials?: boolean },
    ) => putLock(lockId, lock),
  });
}

async function syncLock(lockId: string, flags: number) {
  const result = await axios.post(
    `${BASE_API_URL}/api/locks/${lockId}/sync?flags=${flags}`,
  );
  return result.data;
}

function usePostSyncLock(lockId: string) {
  return useMutation({
    mutationFn: (flags: number) => syncLock(lockId, flags),
  });
}
// Compare this snippet from src/components/AdminLocks/AdminLocks.tsx:
